import React from 'react';
import PropTypes from 'prop-types';

import { Col, Form, Row } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import { faParking } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Select from 'react-select/creatable';

import AttributeInputType from 'lib/enums/AttributeInputType';
import BrandFilter from 'components/global_products/detail_view/BrandFilter';
import DocumentsInputTypeView from 'components/global_products/detail_view/DocumentsInputTypeView';
import GlobalAttributes from 'lib/enums/GlobalAttributes';
import ManufacturerFilter from 'components/global_products/detail_view/ManufacturerFilter';
import MultiValueInputTypeView from 'components/global_products/detail_view/MultiValueInputTypeView';

const CUSTOM_FIELDS = [
  GlobalAttributes.brand_name,
  GlobalAttributes.manufacturer_name,
  GlobalAttributes.private_brand_product_manufacturer_id,
  GlobalAttributes.features_and_benefits_extended,
];

function AttributeValueInputField({
  attribute,
  setIsEditing,
  setAttributeValue,
  isGlobalProductAttribute = false,
}) {
  function custom(attribute) {
    if (attribute.attribute_name === GlobalAttributes.brand_name) {
      return (
        <BrandFilter
          brandID={attribute.brand_id}
          setAttributeValue={e => setAttributeValue(attribute, { id: e?.value, name: e?.label })}
          isGlobalProductAttribute={isGlobalProductAttribute}
          autoFocus={false}
        />
      );
    }
    if (attribute.attribute_name === GlobalAttributes.manufacturer_name) {
      return (
        <ManufacturerFilter
          manufacturerId={attribute.manufacturer_id}
          manufacturerName={attribute.attribute_value}
          setAttributeValue={e => setAttributeValue(attribute, e)}
          isGlobalProductAttribute={isGlobalProductAttribute}
          autoFocus={false}
        />
      );
    }
    if (attribute.attribute_name === GlobalAttributes.private_brand_product_manufacturer_id) {
      const raw = attribute.attribute_value;
      const num = raw != null && raw !== '' ? Number(raw) : NaN;
      const mfrId = Number.isFinite(num) && num > 0 ? num : null;
      return (
        <ManufacturerFilter
          manufacturerId={mfrId}
          setAttributeValue={e => setAttributeValue(attribute, e)}
          isGlobalProductAttribute={isGlobalProductAttribute}
          storeManufacturerId
          autoFocus={false}
        />
      );
    }
    if (attribute.attribute_name === GlobalAttributes.features_and_benefits_extended) {
      return (
        <Form.Control
          as="textarea"
          rows={attribute.attribute_value?.split('\n').length || 1}
          disabled={isGlobalProductAttribute}
          defaultValue={attribute.attribute_value}
          onChange={e => {
            const value = e.target.value;
            setAttributeValue(attribute, value.trim());
            e.target.rows = value.split('\n').length || 1;
          }}
        />
      );
    }
  }
  return (
    <Row className="px-4 py-1">
      <Col>
        {`${attribute.attribute_name}
           ${
             attribute.data_source_type
               ? `| Source - ${attribute.data_source_type}${
                   attribute.data_source_name ? `/${attribute.data_source_name}` : ''
                 }`
               : ''
           } `}
        {attribute.is_propagated && (
          <>
            <FontAwesomeIcon className="ml-2" icon={faParking} />
            <span className="pl-1">{attribute.entity_owner}</span>
          </>
        )}
      </Col>
      <Col>
        {CUSTOM_FIELDS.includes(attribute.attribute_name) ? (
          custom(attribute)
        ) : attribute.input_type === AttributeInputType.DOCUMENT ? (
          <DocumentsInputTypeView
            strJsonObject={attribute.attribute_value}
            setAttributeValue={e => setAttributeValue(attribute, e)}
            isEditView
          />
        ) : attribute.input_type === AttributeInputType.MULTI_VALUE ? (
          <MultiValueInputTypeView
            attributeValues={attribute.attribute_value}
            setAttributeValue={e => setAttributeValue(attribute, e)}
            isEditView
            options={attribute.attribute_options}
          />
        ) : attribute.input_type === AttributeInputType.BOOLEAN ? (
          <Select
            isDisabled={isGlobalProductAttribute}
            className={css(styles.select)}
            defaultValue={{
              label:
                attribute.attribute_value !== null
                  ? attribute.attribute_value === '1' || attribute.attribute_value === 'true'
                    ? 'true'
                    : 'false'
                  : null,
              value:
                attribute.attribute_value !== null
                  ? attribute.attribute_value === '1' || attribute.attribute_value === 'true'
                    ? 'true'
                    : 'false'
                  : null,
            }}
            options={[
              { label: 'true', value: 'true' },
              { label: 'false', value: 'false' },
            ]}
            onChange={e => setAttributeValue(attribute, e.value)}
          />
        ) : attribute.input_type === AttributeInputType.LIST ? (
          <Select
            isDisabled={isGlobalProductAttribute}
            className={css(styles.select)}
            defaultValue={{
              label: attribute.attribute_value,
              value: attribute.attribute_value,
            }}
            options={attribute.attribute_options.map(option => {
              return { label: option, value: option };
            })}
            onChange={e => setAttributeValue(attribute, e?.value)}
            isClearable
          />
        ) : attribute.input_type === AttributeInputType.TEXT_AREA ? (
          <Form.Control
            as="textarea"
            rows={attribute.attribute_value?.split('\n').length || 1}
            disabled={isGlobalProductAttribute}
            defaultValue={attribute.attribute_value}
            onChange={e => {
              const value = e.target.value;
              setAttributeValue(attribute, value.trim());
              e.target.rows = value.split('\n').length || 1;
            }}
          />
        ) : (
          <Form.Control
            type="text"
            disabled={isGlobalProductAttribute}
            defaultValue={attribute.attribute_value || ''}
            onChange={e => setAttributeValue(attribute, e.target.value)}
          />
        )}
      </Col>
    </Row>
  );
}

const styles = StyleSheet.create({
  select: {
    flexGrow: 1,
  },
});

AttributeValueInputField.propTypes = {
  attribute: PropTypes.object,
  setIsEditing: PropTypes.func,
  setAttributeValue: PropTypes.func,
  isGlobalProductAttribute: PropTypes.bool,
};

export default AttributeValueInputField;
